import {
    type CreateStockingCycleInput,
    isValidPlannedWeek,
    plannedStartDateForWeek,
    type StockingCycle,
    type StockingSeason,
    seasonOfDay,
    seasonOfPlannedWeek,
    suggestStockingCycleName,
    type UpdateStockingCycleInput,
} from '@/domain/stockingCycles';
import { getIsoWeek, getIsoWeekYear } from '@/utils/date';

/** A later stocking is usually the next one: the same week half a year ahead. */
const DEFAULT_LEAD_WEEKS = 26;
/** How far ahead a stocking can sensibly be planned. */
export const MAX_YEARS_AHEAD = 10;

export interface NewStockingFormValues {
    season: StockingSeason;
    /** Strings, like every number field in the fish group forms. */
    year: string;
    week: string;
    name: string;
    /** Shown by Edit only; New stocking has no notes field and keeps it empty. */
    notes: string;
}

/**
 * A site's first stocking is usually the one going in now: this week, in today's season as the
 * backend reads it, under the backend's `suggestedName` when there is one. Later stockings
 * default to the same week half a year ahead.
 */
export function makeNewStockingDefaults(
    now: Date,
    existingNames: readonly string[],
    serverSuggestion: string | null = null
): NewStockingFormValues {
    const isFirst = existingNames.length === 0;
    const target = isFirst
        ? now
        : new Date(now.getTime() + DEFAULT_LEAD_WEEKS * 7 * 24 * 60 * 60 * 1000);
    const year = getIsoWeekYear(target);
    const week = getIsoWeek(target);
    const season = isFirst ? seasonOfDay(now) : seasonOfPlannedWeek(year, week);
    const suggestion = suggestStockingCycleName(season, year, existingNames);
    return {
        season,
        year: String(year),
        week: String(week),
        name: (isFirst && serverSuggestion?.trim()) || suggestion,
        notes: '',
    };
}

/**
 * A year from last year to ten years ahead, as a new stocking may be planned. `ownYear` is an
 * existing stocking's own year, which its edit always accepts, however old: only the year it
 * would move to has to be in the window.
 */
export function parseYear(value: string, now: Date, ownYear: number | null = null): number | null {
    const year = Number(value);
    if (!Number.isInteger(year)) return null;
    if (year === ownYear) return year;
    const current = now.getFullYear();
    return year >= current - 1 && year <= current + MAX_YEARS_AHEAD ? year : null;
}

export function parseWeek(value: string, year: number | null): number | null {
    const week = Number(value);
    if (year === null) return Number.isInteger(week) && week >= 1 && week <= 53 ? week : null;
    return isValidPlannedWeek(year, week) ? week : null;
}

/** The week falls in the other half of the year than the season picked (open question N2). */
export function weekContradictsSeason(
    values: Pick<NewStockingFormValues, 'season' | 'year' | 'week'>,
    now: Date,
    ownYear: number | null = null
): boolean {
    const year = parseYear(values.year, now, ownYear);
    const week = parseWeek(values.week, year);
    if (year === null || week === null) return false;
    return seasonOfPlannedWeek(year, week) !== values.season;
}

export function toCreateStockingCycleInput(
    values: NewStockingFormValues,
    now: Date,
    ownYear: number | null = null
): CreateStockingCycleInput | null {
    const year = parseYear(values.year, now, ownYear);
    const week = parseWeek(values.week, year);
    if (year === null || week === null) return null;
    return { name: values.name.trim(), plannedStartDate: plannedStartDateForWeek(year, week) };
}

/**
 * An existing stocking in the New stocking form. Year and week come from its planned start,
 * else its first stocked group, else today; the season from that week, since it isn't stored.
 * The name keeps following season and year only if it is still the suggestion for them.
 */
export function editStockingDefaults(
    cycle: Pick<StockingCycle, 'name' | 'plannedStartDate' | 'stockedFrom' | 'notes'>,
    otherNames: readonly string[],
    now: Date
): { defaults: NewStockingFormValues; followName: string | null } {
    const planned = cycle.plannedStartDate
        ? new Date(`${cycle.plannedStartDate}T12:00:00Z`)
        : (cycle.stockedFrom ?? now);
    const year = getIsoWeekYear(planned);
    const week = getIsoWeek(planned);
    const season = seasonOfPlannedWeek(year, week);
    const suggestion = suggestStockingCycleName(season, year, otherNames);
    return {
        defaults: {
            season,
            year: String(year),
            week: String(week),
            name: cycle.name,
            notes: cycle.notes ?? '',
        },
        followName: cycle.name === suggestion ? cycle.name : null,
    };
}

/**
 * The edit to send. The planned date goes only when the year or week differs from what the
 * form opened with (`initial`): the prefill fills a missing plan from the first stocking or
 * today, and the form only knows weeks, so sending it unasked would give a stocking with no
 * plan one, and move a plan on another day to that week's Monday.
 */
export function toUpdateStockingCycleInput(
    values: NewStockingFormValues,
    initial: Pick<NewStockingFormValues, 'year' | 'week'>,
    now: Date,
    ownYear: number | null
): UpdateStockingCycleInput | null {
    const created = toCreateStockingCycleInput(values, now, ownYear);
    if (!created) return null;
    const notes = values.notes.trim();
    const plannedChanged =
        Number(values.year) !== Number(initial.year) ||
        Number(values.week) !== Number(initial.week);
    return {
        name: created.name,
        ...(plannedChanged ? { plannedStartDate: created.plannedStartDate } : {}),
        notes: notes === '' ? null : notes,
    };
}

/** Groups already stocked before the planned week: the plan is allowed, with a warning. */
export function stockedBeforePlannedWeek(
    stockedFrom: Date | null,
    values: Pick<NewStockingFormValues, 'year' | 'week'>,
    now: Date,
    ownYear: number | null = null
): boolean {
    const year = parseYear(values.year, now, ownYear);
    const week = parseWeek(values.week, year);
    if (!stockedFrom || year === null || week === null) return false;
    return stockedFrom < new Date(`${plannedStartDateForWeek(year, week)}T00:00:00Z`);
}
